import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import SolarIcon from 'components/icons/SolarIcon';
import { copyToClipboard } from 'utils/helpers/copyToClipboard';
import { useToast } from 'context/ToastContext';
import ContactTypeIcon from '../ContactTypeIcon';
import ContactValueField from '../ContactValueField';
import ContactRowActions from '../ContactRowActions';
import { buildContactHref, type ClientContactRow } from '../model/clientContactTypes';

interface ClientContactsTableRowProps {
    row: ClientContactRow;
    disabled: boolean;
    canEdit: boolean;
    editing: boolean;
    editValue: string;
    onEditValueChange: (value: string) => void;
    onEditKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    onStartEdit: () => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onRemove: () => void;
}

const ClientContactsTableRow = ({
    row,
    disabled,
    canEdit,
    editing,
    editValue,
    onEditValueChange,
    onEditKeyDown,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onRemove,
}: ClientContactsTableRowProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showToast } = useToast();
    const href = buildContactHref(row);

    const handleCopy = async () => {
        const ok = await copyToClipboard(editing ? editValue : row.contactValue);
        if (ok) showToast(t('contacts.copySuccess'), 'success');
    };

    return (
        <tr>
            <td className="status">
                <div className="d-flex align-items-center">
                    <ContactTypeIcon type={row.contactType} />
                    <span className="fw-medium">{t(`contacts.types.${row.contactType}`)}</span>
                </div>
            </td>
            <td className={`name ${editing ? '' : 'text-truncate'}`} title={editing ? undefined : row.contactValue}>
                {editing ? (
                    <div className="d-flex align-items-center gap-1">
                        <div className="flex-grow-1 min-w-0">
                            <ContactValueField
                                type={row.contactType}
                                size="sm"
                                autoFocus
                                disabled={disabled}
                                value={editValue}
                                onChange={onEditValueChange}
                                onKeyDown={onEditKeyDown}
                            />
                        </div>
                        <Button
                            size="sm"
                            variant="primary"
                            className="px-2"
                            title={t('common:buttons.save')}
                            disabled={disabled}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={onSaveEdit}
                        >
                            <SolarIcon name="check2" className="fs-14" />
                        </Button>
                    </div>
                ) : href ? (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none d-block text-truncate"
                    >
                        {row.contactValue}
                    </a>
                ) : (
                    <span className="d-block text-truncate">{row.contactValue}</span>
                )}
            </td>
            <td className="action">
                <ContactRowActions
                    rowKey={row.localKey}
                    disabled={disabled}
                    editing={editing}
                    canEdit={canEdit}
                    onStartEdit={onStartEdit}
                    onCancelEdit={onCancelEdit}
                    onCopy={() => void handleCopy()}
                    onRemove={onRemove}
                />
            </td>
        </tr>
    );
};

export default ClientContactsTableRow;
